import axiosInstance from "../../scenes/utils/axiosInstance";
import { API_ENDPOINTS } from "../../config/api";
import { getDoctorSessionCacheKey } from "../../utils/authSession";

const templateCache = new Map();
const inFlightTemplateRequests = new Map();
const CACHE_TTL_MS = 3 * 60 * 1000;

// Pusula aktarım/önizleme işleri ImportJob tablosunu paylaşır ama veri seti değildir.
// BE listeden zaten çıkarır; eski bir BE sürümüne karşı burada da elenir.
export function isPusulaImportJob(job) {
  return job?.metadata?.type === "pusula_bulk";
}

export function clearTemplateCache() {
  templateCache.clear();
  inFlightTemplateRequests.clear();
}

export default class ExcelImportService {
  previewImport = async ({ file, sheet_name, header_row = 1, template_id }) => {
    const formData = new FormData();
    formData.append("file", file);
    if (sheet_name) formData.append("sheet_name", sheet_name);
    formData.append("header_row", String(header_row));
    if (template_id) formData.append("template_id", String(template_id));
    const response = await axiosInstance.post(API_ENDPOINTS.EXCEL_IMPORT.PREVIEW, formData);
    return response.data;
  };

  getTemplates = async (params = {}) => {
    const _uid = getDoctorSessionCacheKey();
    const cacheKey = JSON.stringify({ _uid, ...params });
    const now = Date.now();
    const cached = templateCache.get(cacheKey);
    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
    if (inFlightTemplateRequests.has(cacheKey)) {
      return await inFlightTemplateRequests.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        const response = await axiosInstance.get(API_ENDPOINTS.EXCEL_IMPORT.TEMPLATES, { params });
        templateCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
        return response.data;
      } finally {
        inFlightTemplateRequests.delete(cacheKey);
      }
    })();

    inFlightTemplateRequests.set(cacheKey, fetchPromise);
    return await fetchPromise;
  };

  getTemplateById = async (id) => {
    const response = await axiosInstance.get(API_ENDPOINTS.EXCEL_IMPORT.TEMPLATE_BY_ID(id));
    return response.data;
  };

  createTemplate = async (payload) => {
    const response = await axiosInstance.post(API_ENDPOINTS.EXCEL_IMPORT.TEMPLATES, payload, {
      headers: { "Content-Type": "application/json" },
    });
    clearTemplateCache();
    return response.data;
  };

  updateTemplate = async (id, payload) => {
    const response = await axiosInstance.put(API_ENDPOINTS.EXCEL_IMPORT.TEMPLATE_BY_ID(id), payload, {
      headers: { "Content-Type": "application/json" },
    });
    clearTemplateCache();
    return response.data;
  };

  deleteTemplate = async (id) => {
    const response = await axiosInstance.delete(API_ENDPOINTS.EXCEL_IMPORT.TEMPLATE_BY_ID(id));
    clearTemplateCache();
    return response.data;
  };

  dryRunImport = async ({ file, template_id, create_patients_if_missing = false }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("template_id", template_id);
    formData.append("create_patients_if_missing", String(create_patients_if_missing));
    const response = await axiosInstance.post(API_ENDPOINTS.EXCEL_IMPORT.DRY_RUN, formData);
    return response.data;
  };

  executeImport = async ({ file, template_id, create_patients_if_missing = false }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("template_id", template_id);
    formData.append("create_patients_if_missing", String(create_patients_if_missing));
    const response = await axiosInstance.post(API_ENDPOINTS.EXCEL_IMPORT.EXECUTE, formData);
    return response.data;
  };

  getImportJobs = async (params = {}) => {
    const response = await axiosInstance.get(API_ENDPOINTS.EXCEL_IMPORT.JOBS, { params });
    const data = response.data;
    if (!Array.isArray(data?.jobs)) return data;
    return { ...data, jobs: data.jobs.filter((job) => !isPusulaImportJob(job)) };
  };
}
